import React, { useMemo } from 'react';
import { Box, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import VisibilityOutlinedIcon from '@material-ui/icons/VisibilityOutlined';
import { Publication } from 'src/app/models/business/Publication';
import { InfoTableColumn } from 'src/commons/types';
import { formatPrice } from 'src/utils';
import { PublicationStatusBadge } from '../components/PublicationsHeader/components/PublicationStatusBadge';
import { getPipeCompanyName } from '../components/PublicationFormModal/constants';

const EMPTY_NUMERIC = '-';

const useStyles = makeStyles(() => ({
  productName: {
    fontWeight: 700,
    marginBottom: 4,
  },
  categoryName: {
    fontSize: 12,
    color: '#908376',
  },
  currencyTag: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 28,
    padding: '6px 12px',
    borderRadius: 8,
    background: '#dce9fb',
    border: '1px solid #b8cce9',
    color: '#5b7699',
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
    whiteSpace: 'nowrap',
  },
  truncatedCell: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
}));

const getProductName = (row: Publication) => row.name || row.product_name || 'N/A';
const getCategoryName = (row: Publication) => row.category || row.category_name || '';
const getCompanyName = (row: Publication) => {
  const { company_name, company_id, company } = row;
  if (company_name) return company_name;

  const fromId = getPipeCompanyName(company_id ?? company);
  if (fromId) return fromId;

  if (company && !/^\d+$/.test(String(company))) return company;

  return '-';
};
const getPvpPrice = (row: Publication) => row.PrecioPVP ?? row.pvp_price;
const getPrice = (row: Publication) => {
  if (Array.isArray(row.price)) return row.price[0];
  return row.price;
};
const MAX_VISIBLE_ITEMS = 2;

const formatValueList = (values?: string[]) => {
  if (!values?.length) return 'N/A';
  return values.join(', ');
};

const TruncatedValueList = ({
  values,
  className,
}: {
  values?: string[];
  className?: string;
}) => {
  if (!values?.length) return <>N/A</>;

  const fullText = values.join(', ');

  if (values.length <= MAX_VISIBLE_ITEMS) {
    return <span className={className}>{fullText}</span>;
  }

  const visibleText = `${values.slice(0, MAX_VISIBLE_ITEMS).join(', ')} +${values.length - MAX_VISIBLE_ITEMS}`;

  return (
    <Tooltip title={fullText} arrow>
      <span className={className}>{visibleText}</span>
    </Tooltip>
  );
};

interface UsePublicationColumnsParams {
  onEdit?: (publication: Publication) => void;
  onViewLogs?: (publication: Publication) => void;
}

export const usePublicationColumns = ({ onEdit, onViewLogs }: UsePublicationColumnsParams = {}) => {
  const classes = useStyles();

  return useMemo<InfoTableColumn<Publication>[]>(
    () => [
      {
        id: 'name',
        label: 'Producto',
        minWidth: 220,
        sticky: true,
        orderById: 'name',
        render: (_value, row) => (
          <Box>
            <div className={classes.productName}>{getProductName(row)}</div>
            {getCategoryName(row) && (
              <div className={classes.categoryName}>{getCategoryName(row)}</div>
            )}
          </Box>
        ),
      },
      {
        id: 'publication_id',
        label: 'Pub. ID',
        minWidth: 100,
        render: (_value, row) => row.publication_id || 'N/A',
      },
      {
        id: 'sku',
        label: 'SKU',
        minWidth: 120,
        render: (_value, row) => row.sku || 'N/A'
      },
      {
        id: 'status',
        label: 'Estado',
        minWidth: 100,
        render: (_value, row) => (
          <PublicationStatusBadge
            publicationStateId={row.publication_state_id}
            status={row.status}
          />
        ),
      },
      {
        id: 'currency',
        label: 'Moneda',
        minWidth: 130,
        render: (_value, row) => (
          <span className={classes.currencyTag}>{row.currency_name || 'Pesos Argentinos'}</span>
        ),
      },
      {
        id: 'company',
        label: 'Compañía',
        minWidth: 120,
        orderById: 'company_name',
        render: (_value, row) => getCompanyName(row),
      },
      {
        id: 'pvp_price',
        label: 'Precio PVP',
        minWidth: 110,
        align: 'right',
        render: (_value, row) => {
          const price = getPvpPrice(row);
          if (price === undefined || price === null || price === '') return EMPTY_NUMERIC;
          return formatPrice(price);
        },
      },
      {
        id: 'price',
        label: 'PV',
        minWidth: 100,
        align: 'right',
        render: (_value, row) => {
          const price = getPrice(row);
          if (price === undefined || price === null || price === '') return EMPTY_NUMERIC;
          return formatPrice(price);
        },
      },
      {
        id: 'quantity',
        label: 'Stock',
        minWidth: 80,
        align: 'right',
        render: (_value, { quantity }) => {
          if (quantity === undefined || quantity === null || quantity === '') return EMPTY_NUMERIC;
          return quantity;
        },
      },
      {
        id: 'zones',
        label: 'Zona(s)',
        minWidth: 140,
        render: (_value, row) => formatValueList(row.zones),
      },
      {
        id: 'campaigns',
        label: 'Campaña(s)',
        minWidth: 140,
        render: (_value, row) => formatValueList(row.campaigns),
      },
      {
        id: 'targets',
        label: 'Formas de pago',
        minWidth: 140,
        render: (_value, row) => (
          <TruncatedValueList values={row.targets} className={classes.truncatedCell} />
        ),
      },
      {
        id: 'singlePayOption',
        label: '1 pago tarjetas',
        minWidth: 120,
        render: (_value, row) => row.config_values?.singlePayOption || 'N/A',
      },
      {
        id: 'actions',
        label: 'Acciones',
        minWidth: 100,
        align: 'center',
        render: (_value, row) => (
          <Box display="flex" justifyContent="center" style={{ gap: 4 }}>
            <Tooltip title="Ver historial">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onViewLogs?.(row);
                }}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar publicación">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit?.(row);
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [classes.categoryName, classes.currencyTag, classes.productName, classes.truncatedCell, onEdit, onViewLogs],
  );
};
